import { FC, useEffect, useMemo, useState } from 'react';

import { MetricsProps } from './types';
import { getMetricsConfig, ADS_METRICS_KEYS, DECLINE_CUMULATIVE_METRICS } from './config';
import ModalSettings from '../ModalSettings';
import { trackEvent } from '../../../../../helpers/utils/analytics';
import { generateListKeys } from '../../../../../helpers/utils/reports';
import { useActions } from '../../../hooks/useActions';
import usePermission from '../../../hooks/usePermission';
import { useTypedSelector } from '../../../hooks/useTypedSelector';

/**
 * Универсальный компонент метрик для всех типов отчётов.
 * 
 * Заменяет:
 * - Metrics.tsx (universal)
 * - MetricsOrder.tsx (order)
 * - MetricsPayment.tsx (payment)
 * - MetricsAD.tsx (ad)
 * - MetricsWebinar.tsx (webinar)
 * 
 * Ключи генерируются на основе dataKey из констант:
 * - universal: regs_count (без суффикса)
 * - order: orders_count_2 (суффикс _2)
 * - payment: payments_count_3 (суффикс _3)
 * - webinar: web_users_count_4 (суффикс _4)
 * - ad: ads_clicks_count (без суффикса)
 */
const ReportMetrics: FC<MetricsProps> = ({
    reportType,
    isDisable,
    adsGroupsSelected,
    adsGroupsAvailable,
    setAdsGroupsAvailable,
    setAdsGroupsSelected,
    blockId,
}) => {
    const config = getMetricsConfig(reportType);
    const checkPermission = usePermission();
    const isAdReport = reportType === 'ad';
    
    // Для блоков дашборда читаем из блочного state, иначе из глобального
    const { metrics, createdMetrics } = useTypedSelector((state) => {
        if (blockId) {
            const blockState = state.table.blocks[blockId];
            return {
                metrics: blockState?.metrics ?? state.table.metrics,
                createdMetrics: state.table.createdMetrics, // createdMetrics всегда глобальный
            };
        }
        return state.table;
    });
    const {
        setMetricsChecked,
        setMetrics,
        setMetaTableFetching,
        setMetaChartFetching,
        setMetaCompareChartFetching,
        setMetaCompareTableFetching,
    } = useActions();

    // Данные дерева — ключи стабильны (key = dataKey)
    const [data, setData] = useState(() => 
        config.getData(createdMetrics, checkPermission)
    );

    // Обновляем данные при изменении createdMetrics
    useEffect(() => {
        setData(config.getData(createdMetrics, checkPermission));
    }, [createdMetrics, reportType]);

    const listKeys = useMemo(() => generateListKeys(data), [data]);

    const actionSet = (selected: any) => {
        // Проверяем что selected не пустой и первый элемент существует
        if (!selected || !selected[0]) return;
        
        // Для блоков дашборда передаём blockId в action
        setMetricsChecked({ 
            data: [{ name: selected[0]?.dataKey, label: selected[0]?.label }],
            blockId,
        });
        setMetrics({ data: selected, blockId });

        trackEvent('report-metrics-apply', {
            count: selected.length,
            metrics: selected.map((item: any) => item?.dataKey).filter(Boolean).slice(0, 50),
            report_type: reportType,
        });

        setTimeout(() => {
            setMetaTableFetching({ value: true, blockId });
            setMetaChartFetching({ value: true, blockId });
            setMetaCompareChartFetching({ value: true });
            setMetaCompareTableFetching({ value: true });
        }, 50);
    };

    // Формируем defChecked: используем dataKey, так как ключи в дереве = dataKey
    // Это обеспечивает совместимость со старыми сохранёнными отчётами, где key мог быть в формате "0-0-0"
    const defChecked = useMemo(() => {
        return (metrics ?? []).filter(Boolean).map((item: any) => item?.dataKey).filter(Boolean);
    }, [metrics]);

    return (
        <span data-help-id="report-metrics">
        <ModalSettings
            title="Метрики"
            dataItems={data}
            listKeys={listKeys}
            defSelected={metrics}
            defChecked={defChecked}
            isDisable={isDisable}
            actionSet={actionSet}
            adsGroupsSelected={config.hasAdsMetrics ? adsGroupsSelected : undefined}
            adsGroupsAvailable={config.hasAdsMetrics ? adsGroupsAvailable : undefined}
            setAdsGroupsAvailable={config.hasAdsMetrics ? setAdsGroupsAvailable : undefined}
            setAdsGroupsSelected={config.hasAdsMetrics ? setAdsGroupsSelected : undefined}
            isAdReport={isAdReport}
        />
        </span>
    );
};

export default ReportMetrics;

// Экспорты для обратной совместимости
export const ads_metrics_keys = ADS_METRICS_KEYS;
export const declineCumulativeMetrics = DECLINE_CUMULATIVE_METRICS;
